import { CatalogItemsLimits } from '../../product-catalog-es.types';
import type {
  ResolveEmptySectionIdParams,
  ResolveStickyBoundaryParams,
  ResolveVisibleSectionIdParams,
} from './catalogSectionScroll.types';

const ITEM_LIST_SELECTOR = '[data-testid="virtuoso-item-list"]';

export const USER_SCROLL_EVENTS = ['wheel', 'touchmove'] as const;

export const resolveStickyBoundary = ({
  scrollParent,
  stickyToolbar,
  stickyFilterBar,
}: ResolveStickyBoundaryParams) =>
  Math.max(
    scrollParent.getBoundingClientRect().top,
    stickyToolbar?.getBoundingClientRect().bottom ?? 0,
    stickyFilterBar?.getBoundingClientRect().bottom ?? 0,
  );

export const resolveStickyOffset = ({
  scrollParent,
  stickyToolbar,
  stickyFilterBar,
}: ResolveStickyBoundaryParams) =>
  resolveStickyBoundary({ scrollParent, stickyToolbar, stickyFilterBar }) -
  scrollParent.getBoundingClientRect().top;

const findGroupElement = (scrollParent: HTMLElement, groupIndex: number) =>
  scrollParent.querySelector<HTMLElement>(
    `[data-item-index="${groupIndex}"]:not([data-item-group-index])`,
  );

const isEmptySection = (limit: CatalogItemsLimits) => limit.endRowIndex < limit.startRowIndex;

const resolveEmptySectionIdAbove = ({
  scrollParent,
  sectionLimits,
  groupIndex,
  boundary,
}: ResolveEmptySectionIdParams) => {
  let sectionId: string | undefined;

  for (let index = groupIndex - 1; index >= 0; index -= 1) {
    const limit = sectionLimits[index];

    if (!isEmptySection(limit)) {
      return sectionId;
    }

    const header = findGroupElement(scrollParent, index);

    if (header && header.getBoundingClientRect().bottom > boundary) {
      sectionId = limit.sectionId;
    }
  }

  return sectionId;
};

export const resolveVisibleSectionId = ({
  scrollParent,
  stickyToolbar,
  stickyFilterBar,
  sectionLimits,
}: ResolveVisibleSectionIdParams) => {
  if (!scrollParent || sectionLimits.length === 0) {
    return undefined;
  }

  const boundary = resolveStickyBoundary({ scrollParent, stickyToolbar, stickyFilterBar });
  const rows = Array.from(
    scrollParent.querySelectorAll<HTMLElement>(`${ITEM_LIST_SELECTOR} > [data-item-group-index]`),
  );
  const firstVisibleRow = rows.find((row) => row.getBoundingClientRect().bottom > boundary);

  if (!firstVisibleRow) {
    return resolveEmptySectionIdAbove({
      scrollParent,
      sectionLimits,
      groupIndex: sectionLimits.length,
      boundary,
    });
  }

  const groupIndex = Number(firstVisibleRow.dataset.itemGroupIndex);
  const emptySectionId = resolveEmptySectionIdAbove({
    scrollParent,
    sectionLimits,
    groupIndex,
    boundary,
  });

  return emptySectionId ?? sectionLimits[groupIndex]?.sectionId;
};
